'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { EVENT_CONFIG, getDerivedValues } from '@/lib/config';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const { showUrgency, seatColor } = getDerivedValues();

  // Handle scroll event to add shadow and show floating CTA
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      // Show floating CTA after scrolling past hero section
      setShowFloatingCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (isOpen && !target.closest('nav')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Optimized navigation items for best fit
  const navItems = [
    { name: 'Inicio', link: 'hero' },
    { name: 'Beneficios', link: 'benefits' },
    { name: 'Para quién', link: 'audience' },
    { name: 'El lugar', link: 'venue' },
    { name: 'Formador', link: 'instructor' }
  ];

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm border-b ${scrolled ? 'shadow-md border-gray-200' : 'border-transparent'}`} role="navigation" aria-label="Navegación principal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-4">
              <Link href="/" className="block" aria-label="CoachingXValores - Ir a la página de inicio">
                <span className="text-base sm:text-lg md:text-xl font-heading uppercase tracking-heading font-medium text-black">
                  CoachingXValores
                </span>
              </Link>

            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex md:items-center md:space-x-1 lg:space-x-2">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={`#${item.link}`}
                  className="px-2 py-2 text-xs lg:text-sm font-medium text-black hover:text-[#97d700] cursor-pointer transition-colors uppercase font-heading tracking-heading whitespace-nowrap"
                >
                  {item.name}
                </a>
              ))}
              <a
                href="#registration"
                className="ml-1 lg:ml-2 px-4 py-2.5 text-xs lg:text-sm font-bold text-black bg-[#97d700] hover:bg-[#b5ff00] cursor-pointer transition-all transform hover:scale-105 uppercase font-heading tracking-heading whitespace-nowrap rounded-full shadow-lg"
                aria-label="Solicitar ahora para la certificación"
              >
                APLICAR AHORA
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMenu();
                }}
                className="inline-flex items-center justify-center p-2 rounded-md text-black hover:text-[#97d700] focus:outline-none"
                aria-expanded={isOpen}
                aria-label="Alternar menú de navegación"
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Slide down animation */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } bg-white shadow-lg border-b border-gray-200`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {[...navItems,
              { name: 'Qué incluye', link: 'inclusions' },
              { name: 'Testimonios', link: 'testimonials' }
            ].map((item) => (
              <a
                key={item.name}
                href={`#${item.link}`}
                className="block px-3 py-2 text-base font-medium text-black hover:text-[#97d700] cursor-pointer uppercase font-heading tracking-heading"
                onClick={toggleMenu}
              >
                {item.name}
              </a>
            ))}
            <a
              href="#registration"
              className="block w-full text-center mt-4 px-4 py-3 text-base font-bold text-black bg-[#97d700] hover:bg-[#b5ff00] cursor-pointer uppercase font-heading tracking-heading rounded-full"
              onClick={toggleMenu}
            >
              SOLICITAR ADMISIÓN
            </a>
          </div>
        </div>
      </nav>

      {/* Floating CTA Button */}
      <div className={`fixed bottom-8 right-8 z-50 transition-all duration-500 ${showFloatingCTA ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
        <a
          href="#registration"
          className="group flex items-center gap-3 px-6 py-4 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-lg rounded-full shadow-2xl hover:bg-[#b5ff00] transform hover:scale-110 transition-all duration-300"
          aria-label="Solicitar certificación - botón flotante"
        >
          <span>APLICAR</span>
          {/* Urgency indicator */}
          {showUrgency && (
            <div className="hidden sm:flex items-center gap-2 bg-red-900/10 text-red-600 px-3 py-1 rounded-full">
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: seatColor }}
              ></span>
              <span className="text-xs font-medium">{EVENT_CONFIG.REMAINING_SEATS} plazas</span>
            </div>
          )}
        </a>
      </div>
    </>
  );
}
